///
/// EarthBrowser
///
/// Created by Matt Giger on 1/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

export interface SatelliteGroupProperties extends DatasetFeature {
	id: string
	name: string
	url: string
	active: boolean
	daily_load: boolean
	satellite_count: number
}

const cBatchSize = 100

export class SatelliteGroupsTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists satellite_groups (
					id text primary key,
					name text not null,
					url text not null,
					active integer not null default 1,
					daily_load integer not null default 0,
					satellite_count integer not null default 0
				)`
			},
			{ sql: "create index if not exists idx_satellite_groups_name on satellite_groups(name collate nocase)" },
			{ sql: "create index if not exists idx_satellite_groups_active on satellite_groups(active)" },
		])
	}

	public static async replaceAll(rows: SatelliteGroupProperties[]): Promise<void> {
		await Database.db.exec("delete from satellite_groups", [], "critical")
		await this.insert(rows)
	}

	public static async insert(rows: SatelliteGroupProperties[]): Promise<void> {
		const statement = `insert into satellite_groups (
				id, name, url, active, daily_load, satellite_count
			) values (
				?, ?, ?, ?, ?, ?
			)
			on conflict(id) do update set
				name = excluded.name,
				url = excluded.url,
				active = excluded.active,
				daily_load = excluded.daily_load,
				satellite_count = excluded.satellite_count`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(g => [
				g.id,
				g.name,
				g.url,
				g.active ? 1 : 0,
				g.daily_load ? 1 : 0,
				g.satellite_count,
			]))
		}
	}

	public static async getAll(): Promise<SatelliteGroupProperties[]> {
		const rows = await Database.db.exec(
			"select id, name, url, active, daily_load, satellite_count from satellite_groups order by name",
			[],
			"low"
		) as SatelliteGroupProperties[]

		return rows.map(r => ({
			...r,
			active: Boolean(r.active),
			daily_load: Boolean(r.daily_load),
		}))
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from satellite_groups`,
			[],
			"low"
		) as Array<{ count: number }>
		return Number(rows[0]?.count ?? 0)
	}

	public static async getActive(): Promise<SatelliteGroupProperties[]> {
		const rows = await Database.db.exec(
			"select id, name, url, active, daily_load, satellite_count from satellite_groups where active = 1 order by name",
			[],
			"low"
		) as SatelliteGroupProperties[]

		return rows.map(r => ({
			...r,
			active: Boolean(r.active),
			daily_load: Boolean(r.daily_load),
		}))
	}
}

